/**
 * 「오늘 하루 보지 않기」 — **기기 축**의 저장소(브라우저 `localStorage`만 쓴다).
 *
 * ⚠️ **`packages/ui`에 둘 수 있는 이유가 「브라우저 저장소만 쓴다」는 것이다.** 봤음(서버
 * `PopupView`)은 **계정 축**이라 api 클라이언트가 필요해 앱이 소유하고(호스트에 주입된다),
 * 이쪽은 의존이 `window.localStorage` 하나라 공용 패키지에 산다. 둘을 하나로 합치면
 * 「이 기기에서만 오늘 그만」을 표현할 수 없다(pcon ADR-023 P6).
 */

/**
 * 키에 **계정 id를 넣는 것이 설계다**(pcon ADR-023 P6). 여럿이 쓰는 PC에서 계정 없이 키를 만들면
 * **남이 누른 「오늘 하루」가 내 것이 된다** — 봤음을 서버에 둔 것과 같은 이유이고, 그쪽이 계정 축이면
 * 이쪽은 기기 축이라 **둘 다 필요하다**(하나로 합치면 「이 기기에서만 오늘 그만」을 표현할 수 없다).
 *
 * ⚠️ 접두가 `safetyhub.`인 것은 **web·admin이 같은 키 공간을 쓴다는 뜻이 아니다** — 두 앱은 다른
 * origin에서 서빙되므로 `localStorage`가 이미 갈려 있다. 접두를 앱별로 가르지 않는 것은 그래서
 * 안전하고, 가르면 같은 사람이 두 앱에서 각각 눌러야 하는 지금 동작이 **바뀌지 않는다**(갈려 있다).
 */
function hideTodayKey(viewerId: string, popupId: string): string {
  return `safetyhub.popup.hideToday.${viewerId}.${popupId}`;
}

/** 로컬 날짜(YYYY-MM-DD) — 「오늘」은 사용자가 보는 날짜이므로 UTC로 재지 않는다 */
function localDateKey(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 이 기기에서 오늘 숨겼나.
 *
 * ⚠️ **읽기·쓰기를 전부 try/catch로 감싼다.** `localStorage`는 프라이빗 창·사이트 데이터 차단·
 * 썸네일 캡처에서 **접근 자체가 throw**할 수 있고, 비어서 돌아오기도 한다. 그때의 정답은
 * 「숨기지 않음」(= 팝업을 띄운다)이다 — 저장소가 없다고 팝업이 영영 안 뜨면 공지가 유실된다.
 */
export function isHiddenToday(viewerId: string, popupId: string, now: Date = new Date()): boolean {
  try {
    return window.localStorage.getItem(hideTodayKey(viewerId, popupId)) === localDateKey(now);
  } catch {
    return false;
  }
}

/** 「오늘 하루 보지 않기」 — 실패해도 조용히 넘긴다(다음 방문에 다시 뜨는 것이 안전한 실패다) */
export function hideToday(viewerId: string, popupId: string, now: Date = new Date()): void {
  try {
    window.localStorage.setItem(hideTodayKey(viewerId, popupId), localDateKey(now));
  } catch {
    // 저장 실패 = 다시 뜬다. 공지가 유실되는 반대 방향보다 안전하다
  }
}
